import React from 'react';

import styles from './DialogEditVital.module.css';

import { getUser } from '../../../services/Auth';
import { reverseParseDate } from '../../../services/parseDates';
import { getEnvironmentApiUrl } from '../../../services/General';

import { useRef, useState } from 'react';
import useHttp from '../../../hooks/useHttp';
import { useToast } from '../../../hooks/useToast';

import Input from '../../UI/Input/Input';

import { Parameter } from '../../../interfaces/Profile';
import { DataElement, PhysiologicalMonitoring } from '../../../interfaces/PhysiologicalMonitoring';
import { PatientOutletContext } from '../../../interfaces/Patient';
import { ElementsDispatch } from '../../../interfaces/Reducers';

import { useTranslation } from 'react-i18next';

import { useOutletContext } from 'react-router-dom';

const DialogEditVital: React.FC<{
    vitalSign: {
        param: Parameter
        timestamp: DataElement
    } | null
    modalShow: React.Dispatch<React.SetStateAction<boolean>>,
    dispatchElements: React.Dispatch<ElementsDispatch>
}> = ({ vitalSign, modalShow, dispatchElements }) => {
    const { t } = useTranslation();

    const toast = useToast();

    const { patient, caso }: PatientOutletContext = useOutletContext();

    const { sendRequest: deleteTimestamp } = useHttp();
    const { sendRequest: sendTimestamp } = useHttp<PhysiologicalMonitoring>();

    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const valueRef = useRef<number>(vitalSign?.timestamp.values[vitalSign.param.id] || NaN);
    const commentRef = useRef<string>(vitalSign?.timestamp.comments[vitalSign.param.id + '_comment'] || '');

    const dbId = vitalSign?.timestamp?.values[vitalSign?.param.id + '_db_id'];

    const handleChangeNum = (evt: React.ChangeEvent<HTMLInputElement>) => {
        valueRef.current = evt.target.value.trim() !== ''
            ? Number(evt.target.value)
            : NaN;
    };

    const handleChangeComment = (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
        commentRef.current = evt.target.value;
    };

    const handleDelete = () => {
        const modifyStateDelete = () => {
            if (vitalSign) {
                dispatchElements({
                    type: 'DELETE_PHYSIOLOGICAL',
                    date: vitalSign.timestamp.date,
                    key: vitalSign.param.id
                });
            }
        };

        if (dbId !== null && dbId !== undefined) {
            deleteTimestamp({
                url: getEnvironmentApiUrl() + 'physiological_monitoring/' + dbId,
                method: 'DELETE',
            }, modifyStateDelete, () => {
                toast.error(`${t('error_deleting')} ${vitalSign?.param.name}`);
            });
        }

        modalShow(false);
    };

    const handleValidate = (e: React.FormEvent) => {
        e.preventDefault();

        if (
            vitalSign?.param.max_anomally_limit !== null &&
            vitalSign?.param.max_anomally_limit !== undefined &&
            valueRef.current > vitalSign.param.max_anomally_limit
        ) {
            setErrorMsg(t('value_smaller') + ' ' + vitalSign.param.max_anomally_limit + '.');
            return;
        } else if (
            vitalSign?.param.min_anomally_limit !== null &&
            vitalSign?.param.min_anomally_limit !== undefined &&
            valueRef.current < vitalSign.param.min_anomally_limit
        ) {
            setErrorMsg(t('value_higher') + ' ' + vitalSign.param.min_anomally_limit + '.');
            return;
        }

        const date = reverseParseDate(vitalSign?.timestamp?.date);
        const comment = commentRef.current.trim() !== '' ? { comment: commentRef.current } : {};
        const payload = {
            case_id: caso.id,
            ...comment,
            deleted: false,
            modified: false,
            patient_id: patient.id,
            sheet_parameter_id: vitalSign?.param.id,
            time: date,
            user_id: getUser().id,
            value: isNaN(valueRef.current) ? null : valueRef.current,
        };

        const postState = (data: PhysiologicalMonitoring) => {
            if (vitalSign) {
                dispatchElements({
                    type: 'ADD_PHYSIOLOGICAL',
                    date: vitalSign.timestamp.date,
                    key: vitalSign.param.id,
                    physiological: data,
                    comment: data.comment
                });
            }
        };

        const putState = () => {
            if (vitalSign) {
                dispatchElements({
                    type: 'UPDATE_PHYSIOLOGICAL',
                    date: vitalSign.timestamp.date,
                    key: vitalSign.param.id,
                    value: valueRef.current,
                    comment: comment.comment,
                    userID: getUser().id
                });
            }
        };

        if (dbId === null || dbId === undefined) {
            sendTimestamp({
                url: getEnvironmentApiUrl() + 'physiological_monitoring',
                method: 'POST',
                body: payload
            }, postState, () => {
                toast.error(`${t('error_adding')} ${vitalSign?.param.name}`);
            });
        } else {
            sendTimestamp({
                url: getEnvironmentApiUrl() + 'physiological_monitoring/' + dbId,
                method: 'PUT',
                body: {
                    ...payload,
                    modified: true,
                }
            }, putState, () => {
                toast.error(`${t('error_updating')} ${vitalSign?.param.name}`);
            });
        }

        modalShow(false);
    };

    return (
        <form className='dialog' onSubmit={handleValidate}>
            <h1 className='dialogTitle'>{vitalSign?.param.name}</h1>
            <h2 className='dialogSubTitle'>{vitalSign?.timestamp.date}</h2>
            <div className={styles.content}>
                <Input
                    label=''
                    defaultValue={vitalSign?.timestamp.values[vitalSign.param.id] || undefined}
                    onChange={handleChangeNum}
                    type='number'
                    placeholder={t('enter_value') as string}
                    error={errorMsg !== null}
                    errorMsg={errorMsg}
                />
                <textarea
                    placeholder={t('write_comment') as string}
                    defaultValue={vitalSign?.timestamp.comments[vitalSign.param.id + '_comment'] || undefined}
                    onChange={handleChangeComment}
                ></textarea>
            </div>
            <div className={styles.buttonsContainer}>
                <button type='button' onClick={() => modalShow(false)} className={`${styles.cancel} button`}>
                    {t('cancel')}
                </button>
                <div>
                    <button type='button' onClick={handleDelete} className={`${styles.cancel} button`}>
                        {t('Delete')}
                    </button>
                    <button type='submit' className={`${styles.validate} button`}>
                        {t('validate')}
                    </button>
                </div>
            </div>
        </form>
    );
};

export default DialogEditVital;